/**
 * ScrollList — a vertically scrolling viewport for chat logs, dex tiles, box grids.
 * TOUCH-DRAG (with momentum) AND mouse-wheel, never hover-only. Content is any set
 * of GameObjects laid out in a child container; a geometry mask clips to the
 * viewport and a faint thumb hints at position. Pure scroll math lives in ./scroll.ts.
 *
 * Origin is centered to match the layout `place()` helpers; the viewport rect is
 * therefore [-w/2..w/2, -h/2..h/2] in local space.
 *
 * DRAG vs TAP (the load-bearing behavior). The list must be touch-drag-scrollable even
 * when its INTERACTIVE rows fill the whole viewport (the chat log is the clearest case
 * — its rows leave no empty strip to grab). So drag is tracked at the SCENE input level,
 * bounds-checked to this list's viewport world rect, NOT off a background zone beneath
 * the rows (a press over a filling row would never reach such a zone under the default
 * top-only hit-testing). A press becomes a DRAG only once the pointer travels past a
 * small threshold ({@link DRAG_THRESHOLD}); below that it stays a TAP and the row's own
 * handler fires. While a gesture was a drag, {@link wasDragging} reports true so a row's
 * tap handler can no-op — that is how a drag started over a row scrolls WITHOUT also
 * selecting it. Row handlers therefore fire on POINTER_UP guarded by `!list.wasDragging()`
 * (a tap releases on the row → it fires; a drag releases off the row, or with the flag
 * set, → it does not). No global `scene.input.topOnly` flag is touched.
 */
import Phaser from 'phaser';
import { COLORS, RADIUS } from './theme.js';
import {
  clampScroll,
  isDragGesture,
  stepMomentum,
  thumbGeometry,
  type MomentumState,
} from './scroll.js';

export interface ScrollListOptions {
  readonly width: number;
  readonly height: number;
  /** Wheel sensitivity (px of scroll per wheel unit). */
  readonly wheelSpeed?: number;
}

export class ScrollList extends Phaser.GameObjects.Container {
  /** The scrolling content; callers add items here via addItem(). */
  readonly content: Phaser.GameObjects.Container;
  /**
   * A non-interactive Zone matching the viewport, kept ONLY as a world-bounds reference
   * for {@link pointerOverViewport}: its getBounds() folds in this container's (and any
   * parent container's) full transform, so the drag/wheel bounds-check stays correct even
   * when the list lives inside an offset parent (e.g. the Title save picker). It is NOT
   * interactive and never participates in hit-testing — drag is tracked scene-wide, see
   * the class doc.
   */
  private readonly viewportZone: Phaser.GameObjects.Zone;
  private readonly thumb: Phaser.GameObjects.Graphics;
  private readonly maskGfx: Phaser.GameObjects.Graphics;
  private vpWidth: number;
  private vpHeight: number;
  private contentHeight = 0;
  private readonly wheelSpeed: number;
  private readonly momentum: MomentumState = { offset: 0, velocity: 0 };
  /** True while a pointer is pressed within this viewport (whether or not it became a drag yet). */
  private pressed = false;
  /** True once the current gesture crossed the drag threshold; cleared on the next press. */
  private gestureDragged = false;
  private startPointerX = 0;
  private startPointerY = 0;
  private lastPointerY = 0;
  private lastMoveTime = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, options: ScrollListOptions) {
    super(scene, x, y);
    this.vpWidth = options.width;
    this.vpHeight = options.height;
    this.wheelSpeed = options.wheelSpeed ?? 0.6;

    this.viewportZone = scene.add.zone(0, 0, this.vpWidth, this.vpHeight);
    this.content = scene.add.container(0, 0);
    this.thumb = scene.add.graphics();
    this.maskGfx = scene.make.graphics({}, false);
    this.add([this.viewportZone, this.content, this.thumb]);

    this.applyMask();
    this.setSize(this.vpWidth, this.vpHeight);

    // Drag is tracked at the SCENE input level and bounds-checked to this viewport, so a
    // press that lands on a row (which wins the top-only hit-test) still starts a scroll.
    // The scene events fire regardless of which object is on top under topOnly=TRUE.
    scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.onDown, this);
    scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onMove, this);
    scene.input.on(Phaser.Input.Events.POINTER_UP, this.onUp, this);
    scene.input.on(Phaser.Input.Events.POINTER_WHEEL, this.onSceneWheel, this);

    scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());

    scene.add.existing(this);
  }

  /** Add an item to the scrolling content; pass its full height so layout tracks. */
  addItem(item: Phaser.GameObjects.GameObject & { y: number }, itemHeight: number): this {
    this.content.add(item);
    this.contentHeight += itemHeight;
    this.refreshThumb();
    return this;
  }

  /**
   * Did the CURRENT (or just-finished) gesture become a drag rather than a tap? A row's
   * own tap handler consults this on POINTER_UP and no-ops when true, so a drag started
   * over the row scrolls the list WITHOUT also selecting the row. Stays true until the
   * next press begins, so a row's up handler (which fires just before this widget's
   * scene-level up) reads the correct value.
   */
  wasDragging(): boolean {
    return this.gestureDragged;
  }

  /** Replace the tracked content height (e.g. after a manual relayout). */
  setContentHeight(height: number): this {
    this.contentHeight = height;
    this.momentum.offset = clampScroll(this.momentum.offset, this.contentHeight, this.vpHeight);
    this.applyOffset();
    return this;
  }

  /** Scroll programmatically (clamped). */
  scrollTo(offset: number): this {
    this.momentum.offset = clampScroll(offset, this.contentHeight, this.vpHeight);
    this.momentum.velocity = 0;
    this.applyOffset();
    return this;
  }

  /** Pin to the bottom — the chat hearth's default as new lines arrive. */
  scrollToBottom(): this {
    return this.scrollTo(this.contentHeight);
  }

  get scrollOffset(): number {
    return this.momentum.offset;
  }

  /** Resize the viewport (layout change) and re-clip. */
  resize(width: number, height: number): this {
    this.vpWidth = width;
    this.vpHeight = height;
    this.setSize(width, height);
    // Keep the bounds-reference zone in lockstep so the drag/wheel bounds-check tracks
    // the new viewport exactly.
    this.viewportZone.setSize(width, height);
    this.applyMask();
    this.momentum.offset = clampScroll(this.momentum.offset, this.contentHeight, this.vpHeight);
    this.applyOffset();
    return this;
  }

  override destroy(fromScene?: boolean): void {
    this.scene?.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate, this);
    this.scene?.input?.off(Phaser.Input.Events.POINTER_DOWN, this.onDown, this);
    this.scene?.input?.off(Phaser.Input.Events.POINTER_MOVE, this.onMove, this);
    this.scene?.input?.off(Phaser.Input.Events.POINTER_UP, this.onUp, this);
    this.scene?.input?.off(Phaser.Input.Events.POINTER_WHEEL, this.onSceneWheel, this);
    this.maskGfx.destroy();
    super.destroy(fromScene);
  }

  /**
   * Scene-level press. Bounds-checked to this viewport so a press anywhere in the list —
   * INCLUDING over a row — begins a candidate gesture; a press outside is ignored. We do
   * not yet know if it is a tap or a drag (that is decided on the first threshold-crossing
   * move), so we only record the start and stop any momentum. This fires AFTER the row's
   * own POINTER_DOWN, but rows now act on POINTER_UP, so the row does not select here.
   */
  private onDown(pointer: Phaser.Input.Pointer): void {
    // A fresh press anywhere starts a new gesture, so always clear last gesture's drag
    // flag — even when the press is outside this viewport. This guarantees a clean tap on
    // a row never inherits a stale "dragged" flag from a prior scroll (e.g. a press that
    // lands in a row's touch-slop margin just outside the strict viewport rect).
    this.gestureDragged = false;
    if (!this.pointerOverViewport(pointer)) return;
    this.pressed = true;
    this.momentum.velocity = 0;
    this.startPointerX = pointer.x;
    this.startPointerY = pointer.y;
    this.lastPointerY = pointer.y;
    this.lastMoveTime = performance.now();
  }

  private onMove(pointer: Phaser.Input.Pointer): void {
    if (!this.pressed) return;
    // Below the threshold the press is still a candidate TAP — do not scroll, so a tiny
    // finger wobble on a tappable row never nudges the list nor blocks the row's select.
    if (
      !this.gestureDragged &&
      !isDragGesture(this.startPointerX, this.startPointerY, pointer.x, pointer.y)
    ) {
      return;
    }
    this.gestureDragged = true;
    const dy = pointer.y - this.lastPointerY;
    // Dragging down (positive dy) reveals earlier content => decrease offset.
    this.momentum.offset = clampScroll(
      this.momentum.offset - dy,
      this.contentHeight,
      this.vpHeight,
    );
    const now = performance.now();
    const dt = Math.max(1, now - this.lastMoveTime);
    this.momentum.velocity = -dy / dt;
    this.lastPointerY = pointer.y;
    this.lastMoveTime = now;
    this.applyOffset();
  }

  /**
   * Scene-level release. Ends the press but LEAVES `gestureDragged` set: this fires just
   * AFTER the row's own POINTER_UP (per-object up precedes the scene up in Phaser), so the
   * row's tap guard must still see the drag flag for this gesture. The flag is reset on the
   * next press.
   */
  private onUp(): void {
    this.pressed = false;
  }

  /**
   * Scene-level wheel: scroll only when the pointer is over THIS list's rect (the
   * scene event fires for the whole canvas, so we bounds-check rather than rely on a
   * per-object wheel — which under topOnly would be eaten by whatever row is on top).
   */
  private onSceneWheel(
    pointer: Phaser.Input.Pointer,
    _objects: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void {
    if (!this.pointerOverViewport(pointer)) return;
    this.momentum.velocity = 0;
    this.momentum.offset = clampScroll(
      this.momentum.offset + dy * this.wheelSpeed,
      this.contentHeight,
      this.vpHeight,
    );
    this.applyOffset();
  }

  /**
   * Is the pointer within this list's viewport? Uses the bounds-reference zone's WORLD
   * bounds (Phaser folds in this container's — and any parent container's — full
   * transform), so the check stays correct even when the list lives inside an offset
   * parent container (e.g. the Title save picker).
   */
  private pointerOverViewport(pointer: Phaser.Input.Pointer): boolean {
    const bounds = this.viewportZone.getBounds();
    return bounds.contains(pointer.worldX, pointer.worldY);
  }

  private onUpdate(_time: number, delta: number): void {
    if (this.pressed) return;
    if (this.momentum.velocity === 0) return;
    stepMomentum(this.momentum, delta, this.contentHeight, this.vpHeight);
    this.applyOffset();
  }

  private applyOffset(): void {
    // Content top sits at the viewport top (-h/2), shifted up by the scroll offset.
    this.content.y = -this.vpHeight / 2 - this.momentum.offset;
    this.refreshThumb();
  }

  private applyMask(): void {
    this.maskGfx.clear();
    this.maskGfx.fillStyle(0xffffff, 1);
    // The mask is in world space; align it with this container's position each resize.
    this.maskGfx.fillRect(
      this.x - this.vpWidth / 2,
      this.y - this.vpHeight / 2,
      this.vpWidth,
      this.vpHeight,
    );
    this.content.setMask(this.maskGfx.createGeometryMask());
  }

  private refreshThumb(): void {
    const geo = thumbGeometry(this.momentum.offset, this.contentHeight, this.vpHeight);
    this.thumb.clear();
    if (!geo) return;
    const x = this.vpWidth / 2 - 5;
    const top = -this.vpHeight / 2 + geo.top;
    this.thumb.fillStyle(COLORS.border, 0.7);
    this.thumb.fillRoundedRect(x, top, 3, geo.height, RADIUS.sm);
  }
}
